export type ColumnDef = {
  key: string;
  width?: number;
  suffix?: string;
  computeSuffix?: (item: any) => string;
  computePrefix?: (item: any) => string;
  label?: string;
  value?: string;
};

export const columnLabels: Record<string, string> = {
  quantity: "Ilość sztuk",
  name: "Nazwa",
  purchaseRate: "Kurs zakupu",
  primaryValue: "Koszt nabycia",
  currencyUuid: "Waluta",
  industry: "Branża",
  publicationDate: "Data publikacji na giełdzie",
  area: "Wielkość [w m²]",
  entity: "Podmiot",
  loanType: "Rodzaj",
  interestRate: "Wysokość oprocentowania",
  interestType: "Rodzaj oprocentowania",
  interestPayment: "Odsetki",
  weight: "Masa",
  cut: "Szlif",
  color: "Barwa",
  clarity: "Czystość",
  fineness: "Próba",
  author: "Autor",
  eraAge: "Epoka/wiek",
  contractDate: "Data zawarcia",
  durationDate: "Czas trwania",
  premiumPayment: "Składki",
  autoRenewal: "Automatyczne przedłużenie",
  access: "Dostęp",
  status: "Status",
};

export const getColumns = (slug: string, isRelianceClientView = false, isCompanyDetailsView = false): ColumnDef[] => {
  const hideFinancials = isRelianceClientView && isCompanyDetailsView;
  switch (slug) {
    case "stocks":
    case "shares":
      return [
        { key: "name", width: 250, label: "Nazwa spółki" },
        { key: "industry", width: 130, label: "Branża" },
        ...(!hideFinancials
          ? [{ key: "sharesCount", width: 120, label: "Ilość sztuk" }]
          : []),
        ...(!hideFinancials
          ? [{ key: "sharesValue", width: 130, label: "Koszt nabycia" }]
          : []),
        { key: "currencyUuid", width: 100 },
        {
          key: "publicationDate",
          width: 130,
          label: "Data publikacji na giełdzie",
        },
      ];
    case "crypto":
      return [
        { key: "name", width: 250, label: "Nazwa" },
        ...(!hideFinancials
          ? [{ key: "quantity", width: 130, label: "Ilość" }]
          : []),
        { key: "currencyUuid", width: 120 },
      ];
    case "currencies":
      return [
        { key: "name", width: 250 },
        ...(!hideFinancials
          ? [
              {
                key: "primaryValue",
                width: 130,
                label: "Kwota",
                computeSuffix: (item: any) =>
                  item.currency?.currency ? ` ${item.currency.currency} ` : "",
              },
            ]
          : []),
      ];
    case "treasury_bonds":
      return [
        { key: "", width: 250, label: "Nazwa", value: "Obligacje skarbowe" },
        { key: "interestType", width: 130 },
        { key: "quantity", width: 120, label: "Ilość sztuk" },
        ...(!hideFinancials
          ? [
              {
                key: "primaryValue",
                width: 130,
                label: "Koszt nabycia",
                computeSuffix: (item: any) =>
                  item.currency ? ` ${item.currency?.currency}` : "",
              },
              { key: "currencyUuid", width: 100 },
            ]
          : []),
        { key: "interestRate", width: 160 },
      ];
    case "precious_stones":
      return [
        { key: "name", width: 250 },
        ...(!hideFinancials
          ? [
              {
                key: "primaryValue",
                width: 130,
                label: "Cena zakupu",
                computeSuffix: (item: any) =>
                  item.currency?.currency ? ` ${item.currency.currency}` : "",
              },
              { key: "currencyUuid", width: 120 },
            ]
          : []),
        { key: "weight", width: 130 },
        { key: "cut", width: 115 },
        { key: "color", width: 100 },
        { key: "clarity", width: 100 },
      ];
    case "precious_metals":
      return [
        { key: "name", width: 250 },
        ...(!hideFinancials
          ? [
              {
                key: "primaryValue",
                label: "Cena zakupu",
                width: 130,
                computeSuffix: (item: any) =>
                  item.currency?.currency ? ` ${item.currency.currency}` : "",
              },
              { key: "currencyUuid", width: 120 },
            ]
          : []),
        { key: "weight", width: 130 },
        { key: "fineness", width: 115 },
      ];
    case "artwork":
      return [
        { key: "artworkType", width: 250, label: "Rodzaj" },
        ...(!hideFinancials
          ? [
              {
                key: "primaryValue",
                width: 130,
                label: "Cena zakupu",
                computeSuffix: (item: any) =>
                  item.currency?.currency ? ` ${item.currency.currency}` : "",
              },
              { key: "currencyUuid", width: 120 },
            ]
          : []),
        { key: "name", width: 130, label: "Tytuł" },
        { key: "author", width: 115 },
        { key: "eraAge", width: 115 },
      ];
    case "real_estate":
      return [
        { key: "name", width: 250 },
        ...(!hideFinancials
          ? [
              {
                key: "primaryValue",
                width: 130,
                label: "Cena zakupu",
                computeSuffix: (item: any) =>
                  item.currency?.currency ? ` ${item.currency.currency}` : "",
              },
              { key: "currencyUuid", width: 120 },
            ]
          : []),
        { key: "city", width: 130, label: "Miejscowość" },
        { key: "area", width: 115, computeSuffix: () => " m²" },
        {
          key: "pricePerSqm",
          width: 180,
          label: "Cena za m²",
          computeSuffix: (item: any) =>
            item.currency?.currency ? ` ${item.currency.currency}` : "",
        },
      ];
    case "loans":
      return [
        { key: "entity", width: 250, label: "Podmiot" },
        ...(!hideFinancials
          ? [
              {
                key: "primaryValue",
                width: 120,
                label: "Kwota",
                computeSuffix: (item: any) =>
                  item.currency?.currency ? ` ${item.currency.currency}` : "",
              },
              { key: "interestRate", width: 130 },
              { key: "contractDate", width: 130 },
              { key: "durationDate", width: 100, label: "Czas trwania" },
            ]
          : []),
      ];
    case "investment_policy":
      return [
        { key: "name", width: 250 },
        { key: "currencyUuid", width: 130 },
        { key: "contractDate", width: 120 },
        { key: "durationDate", width: 130 },
        { key: "premiumPayment", width: 150 },
      ];
    default:
      return [
        { key: "name", width: 250 },
        ...(!hideFinancials
          ? [
              {
                key: "primaryValue",
                width: 130,
                label: "Wartość",
                computeSuffix: (item: any) =>
                  item.currency?.currency ? ` ${item.currency.currency}` : "",
              },
              { key: "currencyUuid", width: 120 },
            ]
          : []),
      ];
  }
};
